import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "../design-system";
import { colors } from "../design-system/colors";
import Divider from "../design-system/Divider";
import { ROUTES } from "../../constants/routes";
import { messageContext } from "../../stores/messages/messageContext";
import ReelClipRow from "./ReelClipRow";
import {
  CAPTION_STYLES,
  CaptionStyle,
  ORIENTATIONS,
  Orientation,
  REEL,
  ViewSize,
  exportDone,
  exportFailed,
  exportLabel,
  exportProgress,
} from "./constants/reelText";
import { useClipExport, useShortlist } from "./hooks/useShortlist";
import { captionFormFields, loadMyStyle } from "../grid/export/myStyle";

/**
 * The Reel page — a curated shortlist of postable clips.
 *
 * The ET shows you everything, which is right when you're hunting a specific line and
 * overwhelming when you just want twenty things to post. This is the other door: pick by
 * a recipe, play, tick, export.
 *
 * Nothing renders until you export: each clip plays by seeking the source video, and the
 * posters already exist. Ticking twenty clips queues twenty separate exports.
 */
const ReelPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const messageStore = useContext(messageContext);

  const transcriptId = useMemo(
    () => new URLSearchParams(location.search).get("t"),
    [location.search],
  );

  const { data, loading, error, recipe, setRecipe } = useShortlist(transcriptId);
  const { exportClips, exporting, done, total } = useClipExport();

  const [activeId, setActiveId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [autoPost, setAutoPost] = useState(true);
  const [burnCaptions, setBurnCaptions] = useState(true);
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle>("cherry");
  const [orientation, setOrientation] = useState<Orientation>("original");
  // My Style: start from the saved look, and send its caption details with each export.
  const [styleFields, setStyleFields] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!transcriptId) return undefined;
    let cancelled = false;
    loadMyStyle().then((style: any) => {
      if (cancelled || !style) return;
      if (typeof style.burn_captions === "boolean") setBurnCaptions(style.burn_captions);
      if (CAPTION_STYLES.some((option) => option.key === style.caption_style)) setCaptionStyle(style.caption_style);
      if (ORIENTATIONS.some((option) => option.key === style.orientation)) setOrientation(style.orientation);
      setStyleFields(captionFormFields(style));
    });
    return () => {
      cancelled = true;
    };
  }, [transcriptId]);
  const [size, setSize] = useState<ViewSize>("small");
  // Per-clip shape, picked by clicking a row's poster or its 9:16 preview. A clip with no
  // pick uses the bar's Orientation, which also resets every pick when changed.
  const [shapes, setShapes] = useState<Map<number, Orientation>>(new Map());
  const shapeOf = (id: number): Orientation => shapes.get(id) ?? orientation;
  const chooseShape = (id: number, shape: Orientation) => {
    setShapes((previous) => new Map(previous).set(id, shape));
    // Choosing a shape is choosing the clip.
    setSelected((previous) => (previous.has(id) ? previous : new Set(previous).add(id)));
  };
  const chooseOrientationForAll = (value: Orientation) => {
    setOrientation(value);
    setShapes(new Map());
  };

  const clips = data?.clips ?? [];

  const toggle = (id: number) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Switching recipe swaps the whole list, so a leftover selection would export clips
  // that are no longer on screen.
  const chooseRecipe = (key: string) => {
    setRecipe(key);
    setSelected(new Set());
    setActiveId(null);
  };

  const handleExport = async () => {
    if (!data) return;
    const chosen = clips.filter((clip) => selected.has(clip.id));
    if (!chosen.length) return;

    const { queued, failed } = await exportClips(chosen, {
      transcriptId: data.transcript_id,
      title: data.title,
      autoPost,
      burnCaptions,
      captionStyle,
      orientation,
      orientationFor: (clip) => shapeOf(clip.id),
      styleFields,
    });

    if (queued) messageStore.setMessage(exportDone(queued), "success", 8000);
    if (failed) messageStore.setMessage(exportFailed(failed), "error", 8000);
    if (queued) setSelected(new Set());
  };

  return (
    <Box
      sx={{
        padding: "24px",
        maxWidth: size === "large" ? "1400px" : "980px",
        margin: "0 auto",
        width: "100%",
      }}
    >
      <Box
        component="button"
        onClick={() => navigate(ROUTES.MY_LIBRARY)}
        sx={{
          background: "none",
          border: "none",
          padding: 0,
          marginBottom: "16px",
          color: colors.neutral[600],
          fontFamily: "Outfit, sans-serif",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        {REEL.BACK}
      </Box>

      <Box
        component="h1"
        sx={{
          margin: 0,
          fontFamily: "Outfit, sans-serif",
          fontSize: "28px",
          fontWeight: 600,
          color: colors.neutral.white,
        }}
      >
        {REEL.TITLE}
        {data?.title ? (
          <Box component="span" sx={{ color: colors.neutral[700], fontWeight: 400 }}>
            {" "}
            · {data.title}
          </Box>
        ) : null}
      </Box>

      <Box
        component="p"
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontSize: "15px",
          color: colors.neutral[600],
          marginTop: "8px",
        }}
      >
        {REEL.SUBTITLE}
      </Box>

      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "16px 0" }}>
        {(data?.recipes ?? []).map((option) => {
          const isActive = option.key === recipe;
          return (
            <Box
              key={option.key}
              component="button"
              title={option.blurb}
              onClick={() => chooseRecipe(option.key)}
              sx={{
                padding: "8px 16px",
                borderRadius: "100px",
                border: "none",
                cursor: "pointer",
                fontFamily: "Outfit, sans-serif",
                fontSize: "15px",
                backgroundColor: isActive
                  ? colors.primary[500]
                  : colors.tab.inactiveBackground,
                color: isActive ? colors.neutral.white : colors.neutral[600],
              }}
            >
              {option.label}
            </Box>
          );
        })}

        <Box
          role="group"
          aria-label={REEL.VIEW}
          sx={{ display: "flex", gap: "4px", marginLeft: "auto", alignItems: "center" }}
        >
          {(
            [
              ["small", REEL.VIEW_SMALL],
              ["large", REEL.VIEW_LARGE],
            ] as const
          ).map(([key, label]) => (
            <Box
              key={key}
              component="button"
              aria-pressed={size === key}
              onClick={() => setSize(key)}
              sx={{
                padding: "6px 12px",
                borderRadius: "100px",
                border: `1px solid ${size === key ? colors.primary[500] : "transparent"}`,
                cursor: "pointer",
                fontFamily: "Outfit, sans-serif",
                fontSize: "14px",
                backgroundColor: colors.tab.inactiveBackground,
                color: size === key ? colors.neutral.white : colors.neutral[600],
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      <Divider />

      {loading ? <Status text={REEL.LOADING} /> : null}
      {!loading && error ? <Status text={error} tone="error" /> : null}
      {!loading && !error && !clips.length ? <Status text={REEL.EMPTY} /> : null}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px" }}>
        {clips.map((clip) => (
          <ReelClipRow
            key={clip.id}
            clip={clip}
            videoUrl={data?.video_url ?? ""}
            isActive={activeId === clip.id}
            onPlay={() => setActiveId(clip.id)}
            isSelected={selected.has(clip.id)}
            onToggle={() => toggle(clip.id)}
            size={size}
            captions={burnCaptions}
            captionStyle={captionStyle}
            shape={shapeOf(clip.id)}
            onChooseShape={(shape) => chooseShape(clip.id, shape)}
          />
        ))}
      </Box>

      {clips.length ? (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            marginTop: "24px",
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: colors.background[850],
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="primary"
            disabled={!selected.size || exporting}
            onClick={handleExport}
          >
            {exporting ? exportProgress(done, total) : exportLabel(selected.size)}
          </Button>

          <Button
            variant="text"
            disabled={exporting}
            onClick={() => setSelected(new Set(clips.map((clip) => clip.id)))}
          >
            {REEL.SELECT_ALL}
          </Button>

          {selected.size ? (
            <Button variant="text" disabled={exporting} onClick={() => setSelected(new Set())}>
              {REEL.CLEAR}
            </Button>
          ) : null}

          <Box
            component="label"
            title={REEL.SEND_TO_AUTO_HINT}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily: "Outfit, sans-serif",
              fontSize: "14px",
              color: colors.neutral[600],
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={autoPost}
              disabled={exporting}
              onChange={(event) => setAutoPost(event.target.checked)}
              style={{ width: "16px", height: "16px", accentColor: colors.primary[500] }}
            />
            {REEL.SEND_TO_AUTO}
          </Box>

          <Box
            component="label"
            title={REEL.BURN_CAPTIONS_HINT}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily: "Outfit, sans-serif",
              fontSize: "14px",
              color: colors.neutral[600],
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={burnCaptions}
              disabled={exporting}
              onChange={(event) => setBurnCaptions(event.target.checked)}
              style={{ width: "16px", height: "16px", accentColor: colors.primary[500] }}
            />
            {REEL.BURN_CAPTIONS}
          </Box>

          {burnCaptions ? (
            <BarSelect
              label={REEL.STYLE}
              hint={REEL.STYLE_HINT}
              value={captionStyle}
              options={CAPTION_STYLES}
              disabled={exporting}
              onChange={(key) => setCaptionStyle(key as CaptionStyle)}
            />
          ) : null}

          <BarSelect
            label={REEL.ORIENTATION}
            hint={REEL.ORIENTATION_HINT}
            value={orientation}
            options={ORIENTATIONS}
            disabled={exporting}
            onChange={(key) => chooseOrientationForAll(key as Orientation)}
          />

          <Box
            component="span"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "13px",
              color: colors.neutral[700],
              marginLeft: "auto",
            }}
          >
            {selected.size ? REEL.LIBRARY_HINT : REEL.SELECTED_NONE}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

/** A labelled dropdown in the export bar (Style, Orientation). */
const BarSelect = ({
  label,
  hint,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  options: ReadonlyArray<{ key: string; label: string }>;
  disabled: boolean;
  onChange: (key: string) => void;
}) => (
  <Box
    component="label"
    title={hint}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontFamily: "Outfit, sans-serif",
      fontSize: "14px",
      color: colors.neutral[600],
    }}
  >
    {label}
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontSize: "14px",
        padding: "4px 8px",
        borderRadius: "8px",
        border: `1px solid ${colors.neutral[700]}`,
        backgroundColor: colors.background[850],
        color: colors.neutral.white,
        cursor: "pointer",
      }}
    >
      {options.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  </Box>
);

const Status = ({ text, tone }: { text: string; tone?: "error" }) => (
  <Box
    component="p"
    sx={{
      fontFamily: "Outfit, sans-serif",
      fontSize: "15px",
      padding: "24px 0",
      color: tone === "error" ? colors.primary[500] : colors.neutral[600],
    }}
  >
    {text}
  </Box>
);

export default ReelPage;
